import React from "react";
import { ArrowRight, Linkedin } from "lucide-react";
import basedPrimaryLogo from "/assets/based-primary.png";
import { Link } from "react-router-dom";
import { usePageTracking, trackEvent } from "../hooks/usePageTracking";
import useScrollToTop from "../hooks/useScrollToTop";
import { NavBar } from "./NavBar";
import { cn } from "../utils/cn";
import { Logo } from "./Icons/Logo";
import { DiscordIcon } from "./Icons/DiscordIcon";
import {
  DISCORD_LINK,
  LINKEDIN_LINK,
  TELEGRAM_LINK,
  TWITTER_LINK,
} from "../constants";
import { TwitterIcon } from "./Icons/TwitterIcon";
import { GlobalModalManager } from "./ui/GlobalModalManager";
import { Helmet } from "react-helmet";
import useSetHeightVariable from "../hooks/useSetHeightVariable";

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  hideBanner?: boolean;
}

export function HyperLayout({
  children,
  className,
  isLoading,
  hideBanner,
}: LayoutProps) {
  usePageTracking();
  useScrollToTop();
  useSetHeightVariable();

  return (
    <>
      <style>
        {`
          :root {
            --background: 0 0% 0%;
            --foreground: 0 0% 100%;
            --primary: 13 100% 63%;
          }

          html, body {
            background-color: #000 !important;
            font-size: 1rem;
            line-height: 1.5rem;
          }

          html::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
          }

          .h-affiliate-hero {
            height: calc(var(--vh, 1vh) * 100 + 25px);
          }

          @media (min-width: 768px) {
            .h-affiliate-hero {
              height: calc(var(--vh, 1vh) * 100 - 49.4px + 50px);
            }
          }
        `}
      </style>
      {isLoading && (
        <div
          className={cn(
            "w-screen h-[100dvh] flex justify-center items-center bg-black duration-300 fixed px-3",
            !isLoading ? "opacity-0 z-[-1]" : "opacity-100 z-[9999]"
          )}>
          <Logo className="w-full max-w-72 animate-logo-reveal" />
        </div>
      )}
      <div className="flex flex-col min-h-screen font-inter bg-black">
        {!hideBanner && (
          <div className="border-b border-white/15 text-center py-4 text-sm bg-black text-white hidden md:block z-[100] leading-[0.9rem]">
            Introducing The Next Evolution of BasedApp — The super app of
            Hyperliquid{" "}
            <Link
              to="/2025-06-23-hyperapp"
              target="_blank"
              rel="noreferrer"
              className="ml-2 text-white/80 duration-200 cursor-pointer hover:text-primary inline-flex items-center gap-x-1 border-b border-white/80 border-dashed hover:border-primary">
              Read More <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        <NavBar />

        {/* Main Content */}
        <main
          className={cn(
            "overflow-x-hidden",
            isLoading ? "opacity-0" : "opacity-100",
            className
          )}>
          {children}
        </main>

        {/* Footer - Added to the Layout component */}
        <footer className="bg-black border-white/10 border-t">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center text-primary font-bold text-xl mb-4">
                  <Link to="/" className="flex items-center">
                    <img
                      src={basedPrimaryLogo}
                      alt="BasedApp Logo"
                      className="h-8 w-auto mr-2"
                    />
                  </Link>
                </div>
                <p className="text-white mb-4">
                  Buy, sell and trade crypto with BasedApp on Hyperliquid. Spend
                  crypto with the BasedApp Visa card. Deposit easily from
                  Hyperliquid, Solana and Arbitrum.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/security"
                      className="text-white hover:text-primary">
                      Security
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-white hover:text-primary">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-white hover:text-primary">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/waitlist"
                      onClick={() =>
                        trackEvent(
                          "Navigation",
                          "Click",
                          "Waitlist from Footer"
                        )
                      }
                      className="text-white hover:text-primary">
                      Waitlist
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="https://drive.google.com/drive/folders/16IXfdKFUp8UKyMvT3gWd7mItqgRDDE2a?usp=sharing"
                      onClick={() =>
                        trackEvent(
                          "Navigation",
                          "Click",
                          "Waitlist from Footer"
                        )
                      }
                      className="text-white hover:text-primary">
                      Brand Assets
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Guide</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/2025-03-01-deposit-from-solana"
                      className="text-white hover:text-primary">
                      Deposit from Solana
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/2025-03-01-deposit-from-arbitrum"
                      className="text-white hover:text-primary">
                      Deposit from Arbitrum or Hyperliquid
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/2025-03-01-deposit-from-polygon"
                      className="text-white hover:text-primary">
                      Deposit from Polygon
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/terms" className="text-white hover:text-primary">
                      Platform Terms
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/card-terms"
                      className="text-white hover:text-primary">
                      Card Terms
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="text-white hover:text-primary">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Connect</h3>
                <div className="flex space-x-4">
                  <a
                    href={TWITTER_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-primary">
                    <TwitterIcon className="w-6 h-6" />
                  </a>
                  <a
                    href={DISCORD_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-primary">
                    <DiscordIcon className="w-6 h-6" />
                  </a>
                  <a
                    href={TELEGRAM_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-primary">
                    <svg
                      className="w-6 h-6"
                      viewBox="0 0 24 24"
                      fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.041-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.178.119.13.154.305.164.43-.001.097-.015.185-.049.336z" />
                    </svg>
                  </a>
                  <a
                    href={LINKEDIN_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-primary">
                    <Linkedin className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="text-xxs text-white/50 mb-4 mx-auto leading-[1.4rem]">
                <p className="mb-4">
                  BasedApp is a brand of SHA2 Labs Pte Ltd, which is an exempted
                  entity for Activity F – Digital Payment Token under the
                  Payment Services Act 2019 by the Monetary Authority of
                  Singapore.
                </p>

                <p className="font-semibold mb-2">
                  RISK WARNING ON DIGITAL PAYMENT TOKEN SERVICES
                </p>
                <p className="mb-4">
                  The Monetary Authority of Singapore (MAS) requires us to
                  provide this risk warning to you as a customer of a digital
                  payment token (DPT) service provider.
                </p>

                <p className="mb-2">Before you:</p>
                <ul className="list-disc pl-6 mb-4">
                  <li>
                    pay or transfer your DPT service provider any money or DPT;
                  </li>
                  <li>
                    enter into an agreement with a third party to buy or sell
                    DPTs upon inducement by your DPT service provider;
                  </li>
                  <li>
                    enter into an agreement with a third party under an
                    arrangement by your DPT service provider for the
                    transmission of DPTs;
                  </li>
                  <li>
                    pay a third party any money or DPT under an arrangement by
                    your DPT service provider for the transmission of DPTs; or
                  </li>
                  <li>
                    transfer any digital payment token instrument to your DPT
                    service provider, you should be aware of the following.
                  </li>
                </ul>

                <p className="mb-4">
                  Your DPT service provider is exempted by MAS from holding a
                  licence to provide DPT services. Please note that you may not
                  be able to recover all the money or DPTs you paid to your DPT
                  service provider, or any other third party referred to above,
                  if your DPT service provider's or the third party's business
                  fails.
                </p>

                <p className="mb-4">
                  You should not transact in the DPT if you are not familiar
                  with this DPT. This includes how the DPT is created, and how
                  the DPT you intend to transact is transferred or held by your
                  DPT service provider.
                </p>

                <p className="mb-4">
                  You should be aware that the value of DPTs may fluctuate
                  greatly. You should buy DPTs only if you are prepared to
                  accept the risk of losing all of the money you put into such
                  tokens.
                </p>

                <p className="mb-4">
                  You should be aware that your DPT service provider, as part of
                  its licence to provide DPT services, may offer services
                  related to DPTs which are promoted as having a stable value,
                  commonly known as "stablecoin".
                </p>

                <p className="font-semibold mb-2">IMPORTANT DISCLOSURES</p>
                <p className="mb-4">
                  BasedApp is a fintech company and not a bank. Balances stored
                  on BasedApp represent digital payment tokens as defined under
                  the Payment Services Act 2019 and are not considered as cash
                  deposits. Digital payment tokens are not legal tender, are not
                  endorsed by the government, and do not qualify as a deposit
                  under the Deposit Insurance and Policy Owners' Protection
                  Schemes Act 2011.
                </p>

                <p className="mb-4">
                  BasedApp Visa Card is issued by Xfers Pte Ltd (also known as
                  StraitsX) pursuant to a license from Visa. Visa is a
                  registered trademark of Visa International Service
                  Association. All card transactions are subject to the terms
                  and conditions of the cardholder agreement with Xfers Pte Ltd.
                </p>
              </div>
              <p className="text-left text-white mt-8">
                © {new Date().getFullYear()} BasedApp. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
      <GlobalModalManager />
    </>
  );
}

export default HyperLayout;
